import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Layout, Text, Button, TextField, TextArea} from '@phonectas/unifon-ui-kit';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';

interface CommentEventProps {
	activeCustomer: string;
	filters?: string[];
}

const CommentEvent: React.FC<CommentEventProps> = ({activeCustomer, filters}) => {
	const [t] = useTranslation();
	const {
		addEventComment,
		getAccountFeed,
	} = useThreeSixtyNewStore((state) => ({
		addEventComment: state.addEventComment,
		getAccountFeed: state.getAccountFeed,
	}), shallow);
	const [openTextArea, setOpenTextArea] = useState(false);
	const [comment, setComment] = useState('');
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const handleToggleTextArea = useCallback(() => {
		setOpenTextArea((prev) => !prev);
	}, []);

	const handleClickOutside = useCallback(() => {
		setOpenTextArea(false);
	}, []);

	const handleAddComment = useCallback(() => {
		addEventComment({
			id: activeCustomer,
			comment: comment,
			onSuccess: () => {
				getAccountFeed({id: activeCustomer, feedType: filters || []});
				setComment('');
			}
		});
	}, [activeCustomer, comment, addEventComment, getAccountFeed, filters]);

	useEffect(() => {
		const handleClickOutsideEvent = (event: MouseEvent) => {
			if (
				containerRef.current &&
                !containerRef.current.contains(event.target as Node)
			) {
				handleClickOutside();
				setComment('');
			}
		};

		document.addEventListener('mousedown', handleClickOutsideEvent);
		return () => {
			document.removeEventListener('mousedown', handleClickOutsideEvent);
		};
	}, []);

	useEffect(() => {
		if (openTextArea && textAreaRef.current) {
			textAreaRef.current.focus();
		}
	}, [openTextArea]);

	return (
		<Layout direction="column" fullWidth gap="sm">
			<Layout direction="row" style={{marginTop: '20px'}} justifyContent="space-between" fullWidth>
				<Text color="textSecondary">{t('Add comment')}</Text>
				<Text color="textSecondary">{`${comment.length}/480`}</Text>
			</Layout>
			<Layout direction="row" nowrap fullWidth>
				<div ref={containerRef} style={{width: '100%', display: 'flex', gap: '8px'}}>
					{
						!openTextArea ? (
							<TextField
								fullWidth
								onChange={handleToggleTextArea}
								onClick={handleToggleTextArea}
								placeholder={t('Comment')}
								style={{
									transition: 'opacity 0.3s',
									opacity: openTextArea ? 0 : 1,
								}}
							/>
						) : (
							<TextArea
								ref={textAreaRef}
								rows={3}
								fullWidth
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								placeholder={t('Comment')}
								style={{
									transition: 'opacity 0.3s',
									opacity: openTextArea ? 1 : 0,
								}}
							/>
						)
					}
					<Button
						variant="primary"
						color="success"
						disabled={!comment}
						onClick={handleAddComment}
					>
						{t('Send')}
					</Button>
				</div>
			</Layout>
		</Layout>
	);
}

export default CommentEvent;
